import { AxiosRequest } from "_services/api";
import { HttpError } from "_services/api/HttpError";
import { HttpResponse } from "_services/api/HttpResponse";
import { IData, IHttpError } from "_services/api/interface/HttpResponse";
import { usePaginationSortSearch } from "containers/Table/hooks/usePaginationSortSearch";
import { useCallback, useState } from "react";
import { IListarLoteGerenciarPagamento } from "../interface/IListarLoteGerenciarPagamento";
import { IFiltrosGerenciarPagamento } from "../interface/IFiltrosGerenciarPagamento";
import { IResumoLoteGerenciarPagamento } from "../interface/IDetalheLoteGerenciarPagamento";
import { Data } from "_utils/Data";
import { toastErro } from "components/Toast";

const getMensagemErro = (error: any, fallback: string): string =>
  error?.response?.data?.message ?? fallback;

interface IDescricaoCustoResponse {
  idServico: number;
  nomeServico: string;
}

export function useGerenciarPagamentos() {
  const msAw = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
  const { totalRows, setTotalRows } = usePaginationSortSearch();
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<IListarLoteGerenciarPagamento[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);
  const [filtro, setFiltro] = useState<IFiltrosGerenciarPagamento>({});
  const [selectedRows, setSelectedRows] = useState<
    IListarLoteGerenciarPagamento[]
  >([]);
  const [clearRows, setClearRows] = useState<boolean>(false);
  const [idLoteEscolhido, setIdLoteEscolhido] = useState<number | null>(null);
  const [dadosDetalhes, setDadosDetalhes] =
    useState<IResumoLoteGerenciarPagamento | null>(null);
  const [contadorAguardando, setContadorAguardando] = useState<number | null>(
    null,
  );
  const [contadorAprovados, setContadorAprovados] = useState<number | null>(
    null,
  );
  const [contadorReprovados, setContadorReprovados] = useState<number | null>(
    null,
  );
  const [pagamentoUrgenteAtivo, setPagamentoUrgenteAtivo] =
    useState<boolean>(false);
  const [ordenacao, setOrdenacao] = useState<{
    campo: string;
    direcao: string;
  } | null>(null);

  const buildParams = (
    obj: Record<string, any>,
    dadosFiltros: IFiltrosGerenciarPagamento,
    urgente?: boolean,
  ) => {
    if (dadosFiltros.idsLotes?.length) {
      obj.lotes = JSON.stringify(dadosFiltros.idsLotes);
    }
    if (dadosFiltros.idsNfRecibo?.length) {
      obj.nfRecibos = JSON.stringify(dadosFiltros.idsNfRecibo);
    }
    if (dadosFiltros.idsProcessos?.length) {
      obj.processos = JSON.stringify(dadosFiltros.idsProcessos);
    }
    if (dadosFiltros.idEmpresa) {
      obj.idEmpresa = dadosFiltros.idEmpresa;
    }
    if (dadosFiltros.idsBeneficiarios?.length) {
      obj.beneficiarios = JSON.stringify(dadosFiltros.idsBeneficiarios);
    }
    if (dadosFiltros.placa) {
      obj.placa = dadosFiltros.placa;
    }
    if (dadosFiltros.idsCustos?.length) {
      obj.custos = JSON.stringify(dadosFiltros.idsCustos);
    }
    if (
      dadosFiltros.possuiArquivoRecibo !== null &&
      dadosFiltros.possuiArquivoRecibo !== undefined
    ) {
      obj.possuiArquivoRecibo = dadosFiltros.possuiArquivoRecibo;
    }
    if (dadosFiltros.idUsuario) {
      obj.idUsuario = dadosFiltros.idUsuario;
    }
    if (
      dadosFiltros.periodo &&
      dadosFiltros.periodo[0] &&
      dadosFiltros.periodo[1]
    ) {
      obj.dataInicio = new Data(dadosFiltros.periodo[0]).getDataEua();
      obj.dataFim = new Data(dadosFiltros.periodo[1]).getDataEua();
    }
    if (urgente) {
      obj.pagamentoUrgente = true;
    }
    if (ordenacao?.campo && ordenacao?.direcao) {
      obj.ordenarPor = ordenacao.campo;
      obj.ordem = ordenacao.direcao.toUpperCase();
    }

    return Object.keys(obj)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
      )
      .join("&");
  };

  const listar = useCallback(
    async (dadosFiltros?: IFiltrosGerenciarPagamento, urgente?: boolean) => {
      const filtroAtual = dadosFiltros ?? filtro;
      if (dadosFiltros !== undefined) {
        setFiltro(dadosFiltros);
      }

      setLoading(true);
      setClearRows(true);

      const params = buildParams(
        { pagina: page, limite: limit, statusAprovacao: "PENDENTE" },
        filtroAtual,
        urgente ?? pagamentoUrgenteAtivo,
      );

      try {
        const { status, data } = await msAw.get(`/pagamentos/lote?${params}`);
        setDados(data.itens ?? []);
        setTotalRows(data.meta?.total ?? 0);
        return new HttpResponse(false, status, data);
      } catch (error: IHttpError<IData, any> | any) {
        setDados([]);
        setTotalRows(0);
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
        setClearRows(false);
      }
    },
    [page, limit, filtro, pagamentoUrgenteAtivo, ordenacao],
  );

  const ordenar = (campo: string, direcao: string) => {
    setOrdenacao(campo ? { campo, direcao } : null);
  };

  const buscarContadores = useCallback(async () => {
    try {
      const { data } = await msAw.get(`/pagamentos/lote/contador`);
      setContadorAguardando(
        typeof data.aguardandoAprovacao === "number"
          ? data.aguardandoAprovacao
          : null,
      );
      setContadorAprovados(
        typeof data.aprovados === "number" ? data.aprovados : null,
      );
      setContadorReprovados(
        typeof data.reprovados === "number" ? data.reprovados : null,
      );
    } catch (error) {
      toastErro(getMensagemErro(error, "Erro ao carregar os dados."));
    }
  }, []);

  const buscarDetalhesLote = useCallback(async (idLote: number) => {
    setLoading(true);
    setDadosDetalhes(null);
    try {
      const { status, data } = await msAw.get(
        `/pagamentos/lote/${idLote}/detalhe`,
      );
      setDadosDetalhes(data);
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro(getMensagemErro(error, "Erro ao buscar detalhes do lote"));
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  }, []);

  const aprovarLotes = useCallback(async () => {
    const ids = selectedRows.map((row) => row.id);
    setLoading(true);
    try {
      const { status, data } = await msAw.post(`/pagamentos/lote/aprovar`, {
        ids,
      });
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro(getMensagemErro(error, "Erro ao aprovar lotes"));
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  }, [selectedRows]);

  const reprovarLotes = useCallback(
    async (motivo: string) => {
      const ids = selectedRows.map((row) => row.id);
      setLoading(true);
      try {
        const { status, data } = await msAw.post(`/pagamentos/lote/reprovar`, {
          ids,
          motivo,
        });
        return new HttpResponse(false, status, data);
      } catch (error: IHttpError<IData, any> | any) {
        toastErro(getMensagemErro(error, "Erro ao reprovar lotes"));
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
      }
    },
    [selectedRows],
  );

  const validarProcessos = async (processos: any[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/processo/validar/?processos=${JSON.stringify(processos)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const validarLotes = async (lotes: any[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/pagamentos/lote/validar/lote/?lotes=${JSON.stringify(lotes)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const validarNfRecibos = async (nfRecibos: any[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/pagamentos/lote/validar/nf-recibo/?nfRecibos=${JSON.stringify(nfRecibos)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const baixarReciboNf = async (idLote: number, numeroNfRecibo: string) => {
    setLoading(true);
    try {
      const { data, headers } = await msAw.get(
        `/pagamentos/lote/${idLote}/imagem/recibo`,
        { responseType: "blob" },
      );
      const contentType = headers["content-type"] || "application/octet-stream";
      const ext = contentType.split("/").pop()?.split(";")[0] ?? "bin";
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `recibo_nf_${numeroNfRecibo}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastErro(getMensagemErro(error, "Não foi possível baixar o arquivo"));
    } finally {
      setLoading(false);
    }
  };

  const buscarUsuariosResponsaveis = async (): Promise<
    { value: number; label: string }[]
  > => {
    try {
      const { data } = await msAw.get(`/pagamentos/lote/usuarios-responsaveis`);
      return (data ?? []).map((u: { id: number; nome: string }) => ({
        value: u.id,
        label: u.nome,
      }));
    } catch (error) {
      return [];
    }
  };

  const buscarCustos = async (): Promise<
    { value: number; label: string }[]
  > => {
    try {
      const { data } = await msAw.get<IDescricaoCustoResponse[]>(
        `/financeiro/descricao/`,
      );
      return (data ?? [])
        .map((c) => ({ value: c.idServico, label: c.nomeServico ?? "" }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }),
        );
    } catch (error) {
      return [];
    }
  };

  const onClickProcesso = (param: string) => {
    window.open(
      `${process.env.REACT_APP_DESPACHANTE_BASE_URL}/pages/processo/Processo.php?param=${param}`,
      "_blank",
    );
  };

  const togglePagamentoUrgente = (urgente: boolean) => {
    setPagamentoUrgenteAtivo(urgente);
    listar(filtro, urgente);
  };

  return {
    dados,
    totalRows,
    limit,
    page,
    loading,
    filtro,
    setLimit,
    setPage,
    listar,
    selectedRows,
    setSelectedRows,
    clearRows,
    setClearRows,
    idLoteEscolhido,
    setIdLoteEscolhido,
    dadosDetalhes,
    setDadosDetalhes,
    buscarDetalhesLote,
    buscarContadores,
    contadorAguardando,
    contadorAprovados,
    contadorReprovados,
    aprovarLotes,
    reprovarLotes,
    onClickProcesso,
    baixarReciboNf,
    pagamentoUrgenteAtivo,
    togglePagamentoUrgente,
    ordenacao,
    ordenar,
    validarProcessos,
    validarLotes,
    validarNfRecibos,
    buscarUsuariosResponsaveis,
    buscarCustos,
  };
}
